import { useEffect, useMemo, useRef } from "react";
import { trackEvent } from "@/lib/audit";
import { useLanguage } from "@/hooks/useLanguage";
import { useMatchEvents } from "@/hooks/useMatchEvents";
import { presentationFor } from "@/lib/matchEvents/eventCatalog";
import type { MatchLifecycle } from "@/lib/dataPolicy";
import type { ProviderMatchEvent } from "@/lib/sports/providers/types";

interface Props {
  /** Provider fixture id (API-Football id). When absent, empty state renders. */
  fixtureId?: string | number | null;
  /** Current match lifecycle drives the correct placeholder. */
  lifecycle: MatchLifecycle;
  /** Optional current minute for the header display. */
  minute?: number;
}

/**
 * Real Data Phase 2 — official match events (API-Football `/fixtures/events`).
 *
 * Renderer is 100% driven by provider data. No simulated football events
 * are produced here. Placeholders follow `dataPolicy.ts` per lifecycle.
 */
export function EventTimeline({ fixtureId, lifecycle, minute }: Props) {
  const { t } = useLanguage();
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    void trackEvent("event_timeline_opened", { surface: "below_radar", lifecycle });
  }, [lifecycle]);

  const fixtureKey = fixtureId != null ? String(fixtureId) : undefined;
  const { events, isLoading } = useMatchEvents(fixtureKey);

  // Preserve provider order strictly (never reorder arbitrarily).
  const ordered = useMemo<ProviderMatchEvent[]>(() => events.slice(), [events]);

  const placeholder = (() => {
    if (lifecycle === "SCHEDULED") return t("mev_prematch_empty");
    if (lifecycle === "LIVE") return isLoading ? t("mev_live_waiting") : t("mev_live_waiting");
    return t("mev_finished_none");
  })();

  return (
    <div className="rounded-xl border border-border/60 glass p-2.5">
      <div className="mb-1.5 flex items-center justify-between gap-2 text-[9px] font-bold tracking-widest text-muted-foreground">
        <span>{t("timeline_title")}</span>
        <span className="tabular-nums">
          {typeof minute === "number" && lifecycle === "LIVE" ? `${minute}'` : t("mev_source")}
        </span>
      </div>
      {ordered.length === 0 ? (
        <div className="py-2 text-[10px] text-muted-foreground">{placeholder}</div>
      ) : (
        <ol className="flex flex-col gap-1">
          {ordered.map((e) => {
            const pres = presentationFor(e);
            const minuteLabel =
              e.extraMinute != null && e.extraMinute > 0
                ? `${e.minute}+${e.extraMinute}'`
                : `${e.minute}'`;
            const subject = e.player ?? e.team ?? "";
            const suffix = e.assist ? ` · ${e.assist}` : "";
            return (
              <li
                key={e.key}
                className="flex items-center gap-2 rounded-md border border-border/40 bg-background/30 px-2 py-1 text-[11px]"
                style={{ color: pres.color }}
                title={e.comments ?? undefined}
                aria-label={`${t(pres.labelKey)} ${minuteLabel} ${subject}`}
                data-provider={e.provider}
              >
                <span aria-hidden>{pres.icon}</span>
                <span className="tabular-nums text-foreground/70 min-w-[34px]">{minuteLabel}</span>
                <span className="font-bold">{t(pres.labelKey)}</span>
                {subject && <span className="text-foreground/85 truncate">{subject}{suffix}</span>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}